import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Box } from "@material-ui/core";
import MarkdownWrapper from "../MarkdownWrapper";
import ModalImageCors from "../ModalImageCors";

const useStyles = makeStyles((theme) => ({
  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  stickerMedia: {
    objectFit: "contain",
    width: 150,
    height: 150,
  },
  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: 8,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  commandMessage: {
    fontFamily: "monospace",
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    display: "inline-block",
  },
  errorMessage: {
    color: theme.palette.error.main,
    fontSize: "0.875rem",
    fontStyle: "italic",
    marginTop: theme.spacing(1),
  },
}));

const TelegramMessageRenderer = ({ message }) => {
  const classes = useStyles();

  // Parse Telegram metadata from dataJson
  const getTelegramData = () => {
    try {
      if (message.dataJson) {
        const data = typeof message.dataJson === "string" 
          ? JSON.parse(message.dataJson) 
          : message.dataJson;
        return data.telegramData || {};
      }
    } catch (error) {
      console.error("Error parsing Telegram data:", error);
    }
    return {};
  };

  const telegramData = getTelegramData();

  // Check if message is a command
  const isCommand = message.body && message.body.startsWith("/");

  // Check if there was a download error
  const hasDownloadError = telegramData.downloadError;

  // Render sticker
  if (message.mediaType === "sticker" && message.mediaUrl) {
    return (
      <Box>
        <img
          src={message.mediaUrl}
          alt="Sticker"
          className={classes.stickerMedia}
        />
        {message.body && (
          <Typography variant="body2" style={{ marginTop: 8 }}>
            {message.body}
          </Typography>
        )}
      </Box>
    );
  }

  // Render image
  if (message.mediaType === "image" && message.mediaUrl) {
    return (
      <Box>
        <ModalImageCors imageUrl={message.mediaUrl} />
        {message.body && (
          <Box mt={1}>
            <MarkdownWrapper>{message.body}</MarkdownWrapper>
          </Box>
        )}
      </Box>
    );
  }

  // Render video
  if (message.mediaType === "video" && message.mediaUrl) {
    return (
      <Box>
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
        {message.body && (
          <Box mt={1}>
            <MarkdownWrapper>{message.body}</MarkdownWrapper>
          </Box>
        )}
      </Box>
    );
  }

  // Render document
  if (message.mediaType === "document" && message.mediaUrl) {
    const fileName = telegramData.fileName || "Documento";
    return (
      <Box>
        <a
          href={message.mediaUrl}
          target="_blank"
          rel="noopener noreferrer"
          download
          style={{ textDecoration: "none" }}
        >
          <Box className={classes.downloadMedia}>
            <Typography variant="body2">
              📄 {fileName}
            </Typography>
          </Box>
        </a>
        {message.body && (
          <Box mt={1}>
            <MarkdownWrapper>{message.body}</MarkdownWrapper>
          </Box>
        )}
      </Box>
    );
  }

  // Render command with special formatting
  if (isCommand) {
    return (
      <Box>
        <Typography className={classes.commandMessage}>
          {message.body}
        </Typography>
      </Box>
    );
  }

  // Render download error
  if (hasDownloadError) {
    return (
      <Box>
        <Typography variant="body2">
          {message.body || "Archivo multimedia"}
        </Typography>
        <Typography className={classes.errorMessage}>
          ⚠️ No se pudo descargar el archivo multimedia
        </Typography>
      </Box>
    );
  }

  // Render text message (default)
  return (
    <Box>
      <MarkdownWrapper>{message.body}</MarkdownWrapper>
    </Box>
  );
};

export default TelegramMessageRenderer;
